import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

let sitesCache = null;
export function useSites() {
  const [sites, setSites] = useState(sitesCache || []);
  useEffect(() => {
    if (sitesCache) return;
    api.get("/dailylogs/sites").then(({ data }) => { sitesCache = data; setSites(data); }).catch(() => {});
  }, []);
  return sites;
}
export const resetSitesCache = () => { sitesCache = null; };

export const STATUS_OPTS = [["assigned", "Assigned"], ["reported_complete", "Reported complete"], ["partial", "Partially complete"],
  ["needs_review", "Needs review"], ["verified", "Verified complete"]];

const sel = "h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs";

export function ItemEditor({ it, onSaved, children }) {
  const sites = useSites();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!open) return;
    setF({ status: it.status, task: it.task, site_id: it.site_id || "", unit: it.unit || "", crew: (it.crew_members || []).join(", ") });
    api.get(`/dailylogs/items/${it.id}/sources`).then(({ data }) => setSrc(data)).catch(() => setSrc([]));
  }, [open, it]);
  const save = async () => {
    try {
      await api.patch(`/dailylogs/items/${it.id}`, { status: f.status, task: f.task, site_id: f.site_id, unit: f.unit,
        crew_members: f.crew.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Correction saved — stamps on linked media will refresh"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 space-y-2 p-3" data-testid={`dl-item-editor-${it.id}`}>
        <div className="text-xs font-bold text-slate-900">Correct work item</div>
        <select value={f.status || ""} onChange={(e) => setF({ ...f, status: e.target.value })} className={sel} data-testid="dl-edit-status">
          {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input value={f.task || ""} onChange={(e) => setF({ ...f, task: e.target.value })} className="h-8 text-xs" placeholder="Task" data-testid="dl-edit-task" />
        <div className="flex gap-2">
          <select value={f.site_id || ""} onChange={(e) => setF({ ...f, site_id: e.target.value })} className={sel} data-testid="dl-edit-site">
            <option value="">Location unconfirmed</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Input value={f.unit || ""} onChange={(e) => setF({ ...f, unit: e.target.value })} className="h-8 w-20 text-xs" placeholder="Unit" data-testid="dl-edit-unit" />
        </div>
        <Input value={f.crew || ""} onChange={(e) => setF({ ...f, crew: e.target.value })} className="h-8 text-xs" placeholder="Crew (comma separated)" data-testid="dl-edit-crew" />
        <div className="rounded border border-slate-200 p-1.5 max-h-40 overflow-y-auto" data-testid="dl-item-sources">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Source messages</div>
          {src == null ? <div className="text-[10px] text-slate-400">Loading…</div> : src.length === 0 ? <div className="text-[10px] text-slate-400">No linked messages (AI matched it from the day's context)</div>
            : src.map((m) => <div key={m.id} className="text-[11px] text-slate-700 leading-snug py-0.5 whitespace-pre-line"><span className="text-slate-400">{new Date(m.sent_at).toLocaleTimeString(undefined, { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })} {m.sender_name}:</span> {m.body || `(${m.type})`}</div>)}
        </div>
        {it.history?.length > 0 && <div className="text-[10px] text-slate-400">Edited {it.history.length}× · last by {it.history[it.history.length - 1].by}</div>}
        <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={save} data-testid="dl-edit-save">Save</Button></div>
      </PopoverContent>
    </Popover>
  );
}

export function MediaLabelEditor({ m, onSaved }) {
  const sites = useSites();
  const [f, setF] = useState({ site_id: m.label_site_id || "", unit: m.label_unit || "", task: m.label_task || "", crew: m.label_crew || "", status: m.label_status || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch(`/dailylogs/media/${m.id}/label`, f); toast.success("Label saved and stamp regenerated"); onSaved?.(data); }
    catch (e) { toast.error(apiErrorMessage(e, "Save failed")); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-center" data-testid="dl-media-label-editor">
      <select value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} className={sel} data-testid="dl-label-site">
        <option value="">Site unconfirmed</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="h-8 text-xs" placeholder="Unit" data-testid="dl-label-unit" />
      <Input value={f.task} onChange={(e) => setF({ ...f, task: e.target.value })} className="h-8 text-xs md:col-span-2" placeholder="Work shown" data-testid="dl-label-task" />
      <Input value={f.crew} onChange={(e) => setF({ ...f, crew: e.target.value })} className="h-8 text-xs" placeholder="Crew" data-testid="dl-label-crew" />
      <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={sel} data-testid="dl-label-status">
        <option value="">No completion tag</option><option value="reported_complete">Reported complete</option><option value="verified">Verified complete</option>
      </select>
      <Button size="sm" onClick={save} disabled={busy} className="md:col-span-6 justify-self-end" data-testid="dl-label-save">{busy ? "Saving…" : "Save & regenerate stamp"}</Button>
    </div>
  );
}
